/**
 * SalesPlanTopTen.tsx — /#/sales-plan
 *
 * W66.W6: Top-10 Account Sales Plan workbook — read-only proof of concept.
 * Shows Stellantis fully populated; 9 other accounts render as gray placeholders.
 *
 * Layout:
 *   - Left rail: 10-account list with posture pills
 *   - Right pane: 7-section Plan Detail (Header / ICP Fit / Intel / Relationship+Entry /
 *                 Persona Triad / Checkpoint Snapshot / Cadence Bundle)
 *
 * Visible to ALL authenticated roles (no requireRole guard).
 * In-page tab: "Top-10 Accounts" (default) + "Quota Workbook" (manager+executive only).
 *
 * Canon compliance:
 *   - No exclamation points. No emojis. M-PACT hyphenated.
 *   - BG #F9FAFB, CARD #FFFFFF, BORDER #E5E7EB, TEXT_PRIMARY #111827.
 *   - 5-color UI palette. Posture pills per forecast hex.
 *   - Tenant 1 only. Excludes [SIM-%.
 */

import React, { useState, useEffect } from "react";
import { Link } from "wouter";
import OperatorShell from "@/components/operator/OperatorShell";
import TopTenLeftRail, { type TopTenAccount } from "@/components/sales-plan/TopTenLeftRail";
import PlanDetailView from "@/components/sales-plan/PlanDetailView";
import { getSession, api } from "@/lib/session";

// Canon light theme tokens
const BG = "#F9FAFB";
const BORDER = "#E5E7EB";
const TEXT_PRIMARY = "#111827";
const TEXT_SECONDARY = "#374151";

export default function SalesPlanTopTen() {
  const session = getSession();
  const role: string = (session as any)?.role ?? "ae";
  const isManager = role === "manager" || role === "executive";

  const [accounts, setAccounts] = useState<TopTenAccount[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
  const [selectedAccountName, setSelectedAccountName] = useState<string>("");
  const [planData, setPlanData] = useState<Record<string, any>>({});
  const [planLoading, setPlanLoading] = useState(false);
  const [planError, setPlanError] = useState<string | null>(null);

  // Fetch top-10 account list on mount.
  // api() already throws on non-ok and returns parsed JSON — do NOT call .ok / .json() on it.
  useEffect(() => {
    setListLoading(true);
    api<{ accounts?: TopTenAccount[] }>("/api/sales-plan/top-ten")
      .then((json) => {
        const accts: TopTenAccount[] = json?.accounts ?? [];
        setAccounts(accts);

        // Auto-select first seeded account (Stellantis)
        const first = accts.find((a) => a.seeded && a.id);
        if (first && first.id) {
          setSelectedAccountId(first.id);
          setSelectedAccountName(first.name);
        }
      })
      .catch((err: Error) => setListError(err?.message || "Failed to load Top-10 accounts."))
      .finally(() => setListLoading(false));
  }, []);

  // Fetch plan_data when selection changes
  useEffect(() => {
    if (!selectedAccountId) return;
    setPlanLoading(true);
    setPlanError(null);
    api<{ plan?: { plan_data?: Record<string, any> } }>(`/api/sales-plan/account/${selectedAccountId}`)
      .then((json) => {
        setPlanData(json?.plan?.plan_data ?? {});
      })
      .catch((err: Error) => setPlanError(err?.message || "Failed to load plan."))
      .finally(() => setPlanLoading(false));
  }, [selectedAccountId]);

  const handleSelect = (id: number | null, name: string) => {
    if (!id) return;
    setSelectedAccountId(id);
    setSelectedAccountName(name);
  };

  return (
    <OperatorShell breadcrumbs={[{ label: "Sales Plan" }]}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          background: BG,
          overflow: "hidden",
        }}
      >
        {/* ── Page header / tab bar ──────────────────────────────────────────── */}
        <div
          style={{
            borderBottom: `1px solid ${BORDER}`,
            background: "#FFFFFF",
            padding: "0 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
            {/* Active tab */}
            <div
              style={{
                padding: "14px 20px",
                fontSize: 14,
                fontWeight: 700,
                color: TEXT_PRIMARY,
                borderBottom: "2px solid #111827",
                marginBottom: -1,
                cursor: "default",
              }}
            >
              Top-10 Accounts
            </div>

            {/* Quota Workbook tab — manager/executive only */}
            {isManager && (
              <Link
                href="/sales-plan/quota"
                style={{
                  padding: "14px 20px",
                  fontSize: 14,
                  fontWeight: 500,
                  color: TEXT_SECONDARY,
                  textDecoration: "none",
                  borderBottom: "2px solid transparent",
                  display: "block",
                  marginBottom: -1,
                }}
              >
                Quota Workbook
              </Link>
            )}
          </div>

          <div style={{ fontSize: 12, color: "#9CA3AF" }}>
            Read-only · W66.W6 POC · Stellantis backfilled
          </div>
        </div>

        {/* ── Main content: left rail + right pane ─────────────────────────── */}
        <div
          style={{
            display: "flex",
            flex: 1,
            overflow: "hidden",
          }}
        >
          {/* Left rail */}
          {listLoading ? (
            <div
              style={{
                width: 240,
                borderRight: `1px solid ${BORDER}`,
                background: BG,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#9CA3AF",
                fontSize: 13,
              }}
            >
              Loading...
            </div>
          ) : listError ? (
            <div
              style={{
                width: 240,
                borderRight: `1px solid ${BORDER}`,
                background: BG,
                padding: 16,
                color: "#DC2626",
                fontSize: 13,
              }}
            >
              {listError}
            </div>
          ) : (
            <TopTenLeftRail
              accounts={accounts}
              selectedId={selectedAccountId}
              onSelect={handleSelect}
            />
          )}

          {/* Right detail pane */}
          <PlanDetailView
            accountName={selectedAccountName}
            planData={planData}
            loading={planLoading}
            error={planError}
          />
        </div>
      </div>
    </OperatorShell>
  );
}
